/**
 * Data loading for ScatterHistory (project/all/explore): file timeseries, fleet aggregates, maneuvers.
 */
import { getData, getTimezoneForDate } from '../utils/global';
import { apiEndpoints } from '@config/env';
import { debug as logDebug, error as logError } from '../utils/console';
import { unifiedDataStore } from '../store/unifiedDataStore';
import type { ExploreResampleFetchPlan } from '../utils/timeseriesSeriesTransforms';
import type { ChannelRef } from '../utils/channelRef';

export type ScatterHistoryDataMode = 'server_file' | 'dataset_aggregates' | 'maneuver_stats' | 'start_stats';

/**
 * Serialize a history `filters` object (GRADE/YEAR/EVENT/CONFIG/STATE/SOURCE_NAME) to a
 * `&filters=<JSON>` query fragment. Drops empty arrays; returns '' when nothing to send.
 */
function buildFiltersParam(filters?: Record<string, unknown[]>): string {
  if (!filters) {return '';}
  const cleaned: Record<string, unknown[]> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (Array.isArray(value) && value.length > 0) {cleaned[key] = value;}
  }
  if (Object.keys(cleaned).length === 0) {return '';}
  return `&filters=${encodeURIComponent(JSON.stringify(cleaned))}`;
}

/** Normalize to YYYY-MM-DD for fleet / maneuver APIs */
export function normalizeApiDate(d: string): string {
  const s = String(d).trim();
  if (s.length === 8 && !s.includes('-')) {
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  }
  return s;
}

function parseYmdToUtcMidnight(ymd: string): Date {
  const n = normalizeApiDate(ymd);
  const [y, m, d] = n.split('-').map((x) => parseInt(x, 10));
  return new Date(Date.UTC(y, m - 1, d));
}

/** YYYY-MM-DD strings from start through end (inclusive), UTC calendar stepping */
export function enumerateDaysInclusive(startYmd: string, endYmd: string): string[] {
  const start = parseYmdToUtcMidnight(startYmd);
  const end = parseYmdToUtcMidnight(endYmd);
  if (start.getTime() > end.getTime()) {return [];}
  const out: string[] = [];
  for (let t = start.getTime(); t <= end.getTime(); t += 86400000) {
    const dt = new Date(t);
    const y = dt.getUTCFullYear();
    const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const d = String(dt.getUTCDate()).padStart(2, '0');
    out.push(`${y}-${m}-${d}`);
  }
  return out;
}

export async function fetchScatterHistoryFleetAggregates(options: {
  className: string;
  projectId: number | string;
  startDate: string;
  endDate: string;
  eventType: string;
  agrType: string;
  channels: string[];
  sourceNames: string[];
  signal?: AbortSignal;
}): Promise<unknown[]> {
  const filters: Record<string, string[]> = {};
  if (options.sourceNames.length > 0) {
    filters.SOURCE_NAME = options.sourceNames;
  }
  const sd = normalizeApiDate(options.startDate);
  const ed = normalizeApiDate(options.endDate);
  const filtersParam =
    Object.keys(filters).length > 0 ? `&filters=${encodeURIComponent(JSON.stringify(filters))}` : '';
  const url =
    `${apiEndpoints.app.data}/fleet-performance-data?class_name=${encodeURIComponent(options.className)}` +
    `&project_id=${encodeURIComponent(String(options.projectId))}` +
    `&start_date=${encodeURIComponent(sd)}&end_date=${encodeURIComponent(ed)}` +
    `&event_type=${encodeURIComponent(options.eventType)}` +
    `&agr_type=${encodeURIComponent(options.agrType)}` +
    `&channels=${encodeURIComponent(JSON.stringify(options.channels))}${filtersParam}`;

  const res = await getData(url, options.signal);
  if (!res.success || !Array.isArray(res.data)) {
    logError('[scatterHistoryDataService] fleet-performance-data failed', res);
    return [];
  }
  logDebug('[scatterHistoryDataService] fleet-performance rows', res.data.length);
  return res.data;
}

/** Scatter History page: full date-range aggregate rows (dedicated API, optional limit). */
export async function fetchScatterHistoryAggregatesAll(options: {
  className: string;
  projectId: number | string;
  startDate: string;
  endDate: string;
  eventType: string;
  agrType: string;
  channels: string[];
  sourceNames: string[];
  filters?: Record<string, unknown[]>;
  limit?: number;
  signal?: AbortSignal;
}): Promise<unknown[]> {
  const sd = normalizeApiDate(options.startDate);
  const ed = normalizeApiDate(options.endDate);
  const sourceParam =
    options.sourceNames.length > 0
      ? `&source_names=${encodeURIComponent(JSON.stringify(options.sourceNames))}`
      : '';
  const filtersParam = buildFiltersParam(options.filters);
  let limitParam = '';
  if (options.limit !== undefined && Number.isFinite(options.limit)) {
    const capped = Math.min(50000, Math.max(1, Math.floor(options.limit)));
    limitParam = `&limit=${encodeURIComponent(String(capped))}`;
  }
  const url =
    `${apiEndpoints.app.data}/scatter-history-aggregates?class_name=${encodeURIComponent(options.className)}` +
    `&project_id=${encodeURIComponent(String(options.projectId))}` +
    `&start_date=${encodeURIComponent(sd)}&end_date=${encodeURIComponent(ed)}` +
    `&event_type=${encodeURIComponent(options.eventType)}` +
    `&agr_type=${encodeURIComponent(options.agrType)}` +
    `&channels=${encodeURIComponent(JSON.stringify(options.channels))}` +
    `${limitParam}${sourceParam}${filtersParam}`;

  logDebug('[scatterHistoryDataService] scatter-history-aggregates request', url);
  const res = await getData(url, options.signal);
  if (!res.success || !Array.isArray(res.data)) {
    logError('[scatterHistoryDataService] scatter-history-aggregates failed', res);
    return [];
  }
  logDebug('[scatterHistoryDataService] scatter-history-aggregates rows', res.data.length);
  return res.data;
}

/** Scatter History page: full date-range maneuver rows (dedicated API, optional limit). */
export async function fetchScatterHistoryManeuversAll(options: {
  className: string;
  projectId: number | string;
  startDate: string;
  endDate: string;
  eventType: string;
  channels: string[];
  sourceNames: string[];
  filters?: Record<string, unknown[]>;
  limit?: number;
  signal?: AbortSignal;
}): Promise<unknown[]> {
  if (options.sourceNames.length === 0) {
    logDebug('[scatterHistoryDataService] scatter-history-maneuvers skipped: no source names');
    return [];
  }
  const sd = normalizeApiDate(options.startDate);
  const ed = normalizeApiDate(options.endDate);
  const filtersParam = buildFiltersParam(options.filters);
  let limitParam = '';
  if (options.limit !== undefined && Number.isFinite(options.limit)) {
    const capped = Math.min(50000, Math.max(1, Math.floor(options.limit)));
    limitParam = `&limit=${encodeURIComponent(String(capped))}`;
  }
  const url =
    `${apiEndpoints.app.data}/scatter-history-maneuvers?class_name=${encodeURIComponent(options.className)}` +
    `&project_id=${encodeURIComponent(String(options.projectId))}` +
    `&start_date=${encodeURIComponent(sd)}&end_date=${encodeURIComponent(ed)}` +
    `&event_type=${encodeURIComponent(options.eventType)}` +
    `&channels=${encodeURIComponent(JSON.stringify(options.channels))}` +
    `&source_names=${encodeURIComponent(JSON.stringify(options.sourceNames))}` +
    `${limitParam}${filtersParam}`;

  logDebug('[scatterHistoryDataService] scatter-history-maneuvers request', url);
  const res = await getData(url, options.signal);
  if (!res.success || !Array.isArray(res.data)) {
    logError('[scatterHistoryDataService] scatter-history-maneuvers failed', res);
    return [];
  }
  logDebug('[scatterHistoryDataService] scatter-history-maneuvers rows', res.data.length);
  return res.data;
}

/** Scatter/Probability History page: full date-range start_stats rows (dedicated API, optional limit). */
export async function fetchScatterHistoryStartStatsAll(options: {
  className: string;
  projectId: number | string;
  startDate: string;
  endDate: string;
  channels: string[];
  sourceNames: string[];
  filters?: Record<string, unknown[]>;
  limit?: number;
  signal?: AbortSignal;
}): Promise<unknown[]> {
  if (options.sourceNames.length === 0) {
    logDebug('[scatterHistoryDataService] scatter-history-start-stats skipped: no source names');
    return [];
  }
  const sd = normalizeApiDate(options.startDate);
  const ed = normalizeApiDate(options.endDate);
  const filtersParam = buildFiltersParam(options.filters);
  let limitParam = '';
  if (options.limit !== undefined && Number.isFinite(options.limit)) {
    const capped = Math.min(50000, Math.max(1, Math.floor(options.limit)));
    limitParam = `&limit=${encodeURIComponent(String(capped))}`;
  }
  const url =
    `${apiEndpoints.app.data}/scatter-history-start-stats?class_name=${encodeURIComponent(options.className)}` +
    `&project_id=${encodeURIComponent(String(options.projectId))}` +
    `&start_date=${encodeURIComponent(sd)}&end_date=${encodeURIComponent(ed)}` +
    `&channels=${encodeURIComponent(JSON.stringify(options.channels))}` +
    `&source_names=${encodeURIComponent(JSON.stringify(options.sourceNames))}` +
    `${limitParam}${filtersParam}`;

  logDebug('[scatterHistoryDataService] scatter-history-start-stats request', url);
  const res = await getData(url, options.signal);
  if (!res.success || !Array.isArray(res.data)) {
    logError('[scatterHistoryDataService] scatter-history-start-stats failed', res);
    return [];
  }
  logDebug('[scatterHistoryDataService] scatter-history-start-stats rows', res.data.length);
  return res.data;
}

export async function fetchScatterHistoryFileRows(options: {
  className: string;
  projectId: number;
  startDate: string;
  endDate: string;
  allowedSourceIds: Set<number>;
  requiredChannels: string[];
  channelRefs?: ChannelRef[];
  objectNameBase: string;
  exploreResampleFetchPlan?: ExploreResampleFetchPlan | null;
  onProgress?: (pct: number, msg: string) => void;
  signal?: AbortSignal;
}): Promise<Record<string, unknown>[]> {
  const merged: Record<string, unknown>[] = [];
  const days = enumerateDaysInclusive(options.startDate, options.endDate);
  if (days.length === 0) {return merged;}

  let step = 0;
  const estimateSteps = Math.max(1, days.length * 4);

  for (const dateDisplay of days) {
    if (options.signal?.aborted) {break;}
    const timezone = await getTimezoneForDate(options.className, options.projectId, dateDisplay);
    if (options.signal?.aborted) {break;}
    let url = `${apiEndpoints.app.datasets}/date/dataset_id?class_name=${encodeURIComponent(options.className)}&project_id=${encodeURIComponent(options.projectId)}&date=${encodeURIComponent(dateDisplay)}`;
    if (timezone) {url += `&timezone=${encodeURIComponent(timezone)}`;}
    const resp = await getData(url, options.signal);
    step++;
    options.onProgress?.(Math.min(92, Math.floor((step / estimateSteps) * 90)), `Listing datasets ${dateDisplay}...`);
    if (!resp.success || !resp.data) {continue;}
    const list: unknown[] = Array.isArray(resp.data) ? resp.data : [];
    for (const row of list) {
      if (options.signal?.aborted) {break;}
      const r = row as Record<string, unknown>;
      const sid = Number(r.source_id ?? r.sourceId);
      const sname = String(r.source_name ?? r.sourceName ?? '').trim();
      const dsid = Number(r.dataset_id ?? r.datasetId);
      if (!Number.isFinite(sid) || !Number.isFinite(dsid) || !sname) {continue;}
      if (options.allowedSourceIds.size > 0 && !options.allowedSourceIds.has(sid)) {continue;}

      // Dataset-level metadata for client-side Year/Event filtering (server_file mode).
      const yearName = String(r.year_name ?? r.yearName ?? '').trim();
      const eventName = String(r.event_name ?? r.eventName ?? '').trim();

      const dateNorm = dateDisplay.replace(/-/g, '');
      const data = await unifiedDataStore.fetchDataWithChannelCheckingFromFile(
        'scatter',
        options.className,
        String(sid),
        options.requiredChannels,
        {
          projectId: String(options.projectId),
          className: options.className,
          datasetId: String(dsid),
          sourceName: sname,
          date: dateNorm,
          timezone: timezone || undefined,
          objectName: `${options.objectNameBase}_ds${dsid}_${dateNorm}`,
          use_v2: true,
          applyGlobalFilters: false,
          skipTimeRangeFilter: true,
          exploreResampleFetchPlan: options.exploreResampleFetchPlan ?? undefined,
          channelRefs: options.channelRefs?.length ? options.channelRefs : undefined,
        },
        'timeseries'
      );
      step++;
      options.onProgress?.(Math.min(92, Math.floor((step / estimateSteps) * 90)), `Loading ${sname} ${dateDisplay}...`);
      if (!Array.isArray(data)) {continue;}
      for (const pt of data) {
        merged.push({
          ...(pt as Record<string, unknown>),
          source_name: sname,
          SOURCE_NAME: sname,
          year_name: yearName,
          event_name: eventName,
        });
      }
    }
  }
  return merged;
}
